import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { UserTypeContext } from "../../AppRoute";
import Videomanualcontroller from '../videomanualcontroller/videomanualcontroller';
import candidateanalysis from '../../apis/candidatescreening/candidateanalysis';
import Transcript from '../transcript/transcript';
import Dialog from '@mui/material/Dialog';
import Playicon from '../Icons/playicon';
import Tooltip from '@mui/material/Tooltip';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { CandidateAnalysisPrintButton } from './candidateanalysispdf';

const jsonConfig = require("../../Config.json");

const CandidateAnalysisAi = () => {
  const { id } = useParams();
  const history = useNavigate();
  const { userRecords } = useContext(UserTypeContext);
  const [showMore, setShowMore] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interviewstatus, setInterviewstatus] = useState("");
  const [violationsData, setViolationsData] = useState({
    faceViolations: [],
    voiceViolations: [],
    browserViolations: []
  });
  const [interviewDuration, setInterviewDuration] = useState(0);
  const [toggleAiScreen, setToggleAiScreen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedViolationTimestamp, setSelectedViolationTimestamp] = useState(null);

  const fetchDurationfromTranscript = async () => {
    try {
      const url = `${jsonConfig.ctResourcesCdn}/screeninginterview/${id}/transcript/${id}.txt`;
      const response = await axios.get(url);

      let segments = [];

      let durationSeconds = response.data?.durationSec || null;
      if (!durationSeconds) {
        const parsed = JSON.parse(response.data + "{} ] }");
        segments = parsed?.segments || [];
        segments.pop();
        durationSeconds = segments[segments.length - 1]?.currentDurationSec || null;
      }
      return durationSeconds;
    } catch (err) {
      console.error("Failed to fetch transcript", err);
    }
  };

  // Fetch candidate analysis data
  useEffect(() => {
    const fetchAnalysisData = async () => {
      try {
        let payload = {
          candidatescreeningid: id,
          recruitmentpartnerId: userRecords?.recruitmentpartnerId
        }
        const response = await candidateanalysis(payload);
        const data = response?.data?.data ?? null;
        setAnalysisData(data);
        setViolationsData({
          faceViolations: data?.violations?.face?.events || [],
          voiceViolations: data?.violations?.voice?.events || [],
          browserViolations: data?.violations?.browser?.events || []
        })
        setInterviewstatus(data?.statusdetails?.code || "");
        let duration = data?.durationSec || null;
        if (!duration) {
          duration = await fetchDurationfromTranscript();
        }
        setInterviewDuration(duration);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error('Error fetching candidate analysis:', err);
      }
    };
    fetchAnalysisData();
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Format duration
  const formatDuration = (secs) => {
    if (secs == null || isNaN(secs)) return '00:00';
    const s = Math.floor(Number(secs));
    const hours = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${mins}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get interview status display text
  const getInterviewStatusDisplay = () => {
    if (interviewstatus === 'completed') {
      return 'Completed';
    } else if (interviewstatus === 'initiated') {
      return 'Initiated but not completed';
    } else if (interviewstatus === 'interrupted') {
      return 'Interrupted by candidate';
    }
    return 'Unknown';
  };

  // Get status CSS class
  const getStatusClass = () => {
    if (interviewstatus === 'completed') {
      return 'status-completed';
    } else if (interviewstatus === 'initiated') {
      return 'status-initiated-not-completed';
    } else if (interviewstatus === 'interrupted') {
      return 'status-candidate-interrupt';
    }
    return 'status-unknown';
  };

  if (loading) {
    return (
      <div className='loading_interview_analysis'>
        <CircularProgress />
      </div>
    );
  }

  const questions = analysisData?.candidatescreeningqas?.qa || [];

  const getViolationName = (val) => {
    let name = val.replaceAll("_", " ").toLowerCase();
    let firstLetter = name.charAt(0).toUpperCase();
    name = firstLetter + name.slice(1);
    return name;
  }

  const getViolationTimestamp = (sec) => {
    if (isNaN(sec)) return '0:00';
    const s = Number(sec);
    const mins = Math.floor(s / 60);
    const seconds = s % 60;
    return `${mins}:${seconds.toString().padStart(2, '0')}`;
  }

  const interviewVideoUrl = `${jsonConfig.ctResourcesCdn}/screeninginterview/${id}/video/${id}.${analysisData?.extension || 'webm'}`;
  const interviewTranscriptUrl = `${jsonConfig.ctResourcesCdn}/screeninginterview/${id}/transcript/${id}.txt`;

  const gotoDashBoard = () => {
    history('/jdcandidates', {
      state: {
        toggleAiScreen: true,
        jdId: analysisData?.jobdescriptiondetails?._id,
      },
    })
  }

  return (
    <>
      <div className='maincontainer-interview-analysis'>
        <div className='top-container-interview-analysis'>
          <div className="reportHeader_jdCandidateList">
            <Tooltip title={"Go Back to Candidate List"} placement="bottom">
              <div className="goBackButton_jdCandidateList" onClick={(e) => gotoDashBoard()}>
                <ArrowBack sx={{ fontSize: 20 }} />
              </div>
            </Tooltip>
            <div className="reportTitle_jdCandidateList">Analysis</div>
          </div>
        </div>
        
        <div className='candidate-info-card'>
          <div className='left-container-candidateinfo'>
            <span className='candidate-name'>{analysisData?.name}</span>
            <span className='sub-items-candidate-info'>{analysisData?.email}</span>
            <div className='interview-info-container'>
              <span className='sub-items-candidate-info'>Position : {analysisData?.jobdescriptiondetails?.jobTitle}</span>
              <span className='sub-items-candidate-info'>Interview Date : {formatDate(analysisData?.createdAt)}</span>
              <span className='sub-items-candidate-info'>Duration : {formatDuration(interviewDuration)}</span>
            </div>
            <div className='interview-status-container'>
              <span className='status-label'>Interview Status:</span>
              <span className={`status-badge ${getStatusClass()}`}>
                {getInterviewStatusDisplay()}
              </span>
            </div>
          </div>
          <div className='right-container-ratings'>
            
            <div className='rating-card rating-overall'>
              <span className='rating-label'>Overall Rating</span>
              <span className='rating-value'>{analysisData?.score}</span>
            </div>

          <CandidateAnalysisPrintButton
            analysisData={analysisData}
            interviewDuration={interviewDuration}
            interviewStatus={interviewstatus}
            interviewStatusDisplay={getInterviewStatusDisplay()}
            questions={questions}
            videoUrl={interviewVideoUrl}
            transcriptUrl={interviewTranscriptUrl}
            formatDuration={formatDuration}
            formatDate={formatDate}
          />
          </div>
        </div>

        {/* Interview Violations Section */}

        {violationsData?.faceViolations?.length === 0 && violationsData?.voiceViolations?.length === 0 && violationsData.browserViolations?.length === 0 ? null : (
          <Accordion className='violations-accordion' expanded={expanded === 'panel1'} onChange={handleChange('panel1')} >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="violations-content"
              id="violations-header"
              className='violations-accordion-summary'
            >
              <Typography component="span" className='violations-title'>
                Interview Violations ({violationsData?.faceViolations?.length + violationsData?.voiceViolations?.length + violationsData?.browserViolations?.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails className='violations-accordion-details'>
              {/* Face Violations */}
              {violationsData.faceViolations.length > 0 && (
                <div className='violation-category'>
                  <div className='violation-category-header'>
                    <span className='violation-dot'></span>
                    <span className='violation-category-title'>Face Violations ({violationsData.faceViolations.length})</span>
                  </div>
                  {violationsData.faceViolations.map((violation, index) => (
                    <div key={`face-${index}`} className='violation-item'>
                      <div className='violation-info'>
                        <div className='violation-text'>{getViolationName(violation?.type)}</div>
                        <div className='violation-timestamp'> <span className="timestamp-icon cp" onClick={() => { setSelectedViolationTimestamp(violation.atSec); setExpanded(false) }}><Playicon fill='black' /></span>Detected at : {getViolationTimestamp(violation.atSec)}</div>
                      </div>
                      <span className={`violation-severity severity-${violation.severity}`}>
                        {violation.severity}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Voice Violations */}
              {violationsData.voiceViolations.length > 0 && (
                <div className='violation-category'>
                  <div className='violation-category-header'>
                    <span className='violation-dot'></span>
                    <span className='violation-category-title'>Voice Violations ({violationsData.voiceViolations.length})</span>
                  </div>
                  {violationsData.voiceViolations.map((violation, index) => (
                    <div key={`voice-${index}`} className='violation-item'>
                      <div className='violation-info' >
                        <div className='violation-text'>{getViolationName(violation?.type)}</div>

                        <div className='violation-timestamp'><span className="timestamp-icon cp" onClick={() => { setSelectedViolationTimestamp(violation.atSec); setExpanded(false) }}><Playicon fill='black' /></span>Detected at : {getViolationTimestamp(violation.atSec)}</div>
                      </div>
                      <span className={`violation-severity severity-${violation.severity}`}>
                        {violation.severity}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {violationsData.browserViolations.length > 0 && (
                <div className='violation-category'>
                  <div className='violation-category-header'>
                    <span className='violation-dot violation-dot-browser'></span>
                    <span className='violation-category-title'>Browser Violations ({violationsData.browserViolations.length})</span>
                  </div>
                  {violationsData.browserViolations.map((violation, index) => (
                    <div key={`browser-${index}`} className='violation-item'>
                      <div className='violation-info'>
                        <div className='violation-text'>{getViolationName(violation?.type)}</div>
                        <div className='violation-timestamp'><span className="timestamp-icon cp" onClick={() => setSelectedViolationTimestamp(violation.atSec)}><Playicon fill='black' /></span>Detected at : {getViolationTimestamp(violation.atSec)}</div>
                      </div>
                      <span className={`violation-severity severity-${violation.severity}`}>
                        {violation.severity}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        <div className='interview-content-container'>

          <div className='left-side-container-interview-infos'>
            <div className='interview-video-container'>
              <span className='jd-heading'>Interview Video</span>
              <Videomanualcontroller
                src={`${jsonConfig.ctResourcesCdn}/screeninginterview/${id}/video/${id}.${analysisData?.extension || 'webm'}` || ''}
                interviewDuration={interviewDuration}
                violationAtSec={selectedViolationTimestamp}
              />
              <div className='video-placeholder'>Interview video is omitted in this PDF. Please view it in the portal.</div>
            </div>
            {toggleAiScreen &&
              <div className='jdcontainer-interview-infos'>
                <span className='jd-heading'>Job Description</span>
                <div className={`jd-content ${showMore ? 'expanded' : 'collapsed'}`}>
                  {analysisData?.jobdescriptiondetails?.jobDesc}
                </div>
                <button
                  className='show-more-btn'
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ? 'Show Less' : '... Show More'}
                </button>
              </div>
            }
          </div>

          <div className='right-side-container-interview-infos'>
            <div className='question-answer-analysis-container'>
              <div className='question-answer-analysis-container-header'>
                
                <span className='qa-heading'>Question-by-Question Analysis</span>
                <button
                  className='transcript-btn'
                  onClick={() => setShowTranscript(true)}
                >
                  Transcript
                </button>
              </div>
              {questions.map((qa, index) => (
                <Accordion key={index} className='qa-accordion'>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    className='qa-accordion-summary'
                  >
                    <div className='accordion-header-content'>
                      <Typography component="span" className='question-number'>Q{index + 1}</Typography>
                      <Typography component="span" className='question-text'>{qa.question}</Typography>
                      <div className='question-meta'>
                        <span className='question-rating'>{qa.score ?? qa.rating}</span>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className='qa-accordion-details'>
                    <div className='answer-container'>
                      <Typography component="div" className='answer-label'>Answer:</Typography>
                      <Typography component="div" className='answer-text'>{qa.answer}</Typography>
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </div>

          <Dialog
            open={showTranscript}
            onClose={() => setShowTranscript(false)}
            fullScreen
          >
            <Transcript
              id={id}
              onClose={() => setShowTranscript(false)}
            />
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default CandidateAnalysisAi;